/**
 * PaymentRemindersSettings - Automatische Zahlungserinnerungen
 * Extrahiert aus SettingsPage.js für bessere Wartbarkeit
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentRemindersSettings = () => {
  const [reminderSettings, setReminderSettings] = useState({
    enabled: false,
    first_reminder_days: 7,
    second_reminder_days: 14,
    third_reminder_days: null,
    max_reminders: 3,
    first_reminder_template_id: '',
    second_reminder_template_id: '',
    third_reminder_template_id: null,
    send_email: true,
    send_whatsapp: false,
    escalation_enabled: true,
    escalation_days: 5,
    escalation_email: ''
  });
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [settingsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/reminder/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/email-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);

      if (settingsRes.data) {
        setReminderSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
      if (templatesRes.data) {
        setEmailTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveReminderSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/reminder/settings`, reminderSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Erinnerungs-Einstellungen gespeichert');
    } catch (error) {
      console.error('Error saving reminder settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              Automatische Zahlungserinnerungen
            </CardTitle>
            <CardDescription>
              Konfigurieren Sie automatische Erinnerungen für überfällige Rechnungen
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable/Disable */}
        <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
          <div className="flex-1">
            <h4 className="font-semibold text-lg">Automatische Erinnerungen aktivieren</h4>
            <p className="text-sm text-gray-600 mt-1">
              Wenn aktiviert, können Zahlungserinnerungen automatisch versendet werden
            </p>
          </div>
          <Switch
            checked={reminderSettings.enabled}
            onCheckedChange={(checked) => setReminderSettings({...reminderSettings, enabled: checked})}
          />
        </div>

        {/* Timing Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Label>1. Erinnerung (Tage nach Fälligkeit)</Label>
            <Input
              type="number"
              min="1"
              max="30"
              value={reminderSettings.first_reminder_days}
              onChange={(e) => setReminderSettings({
                ...reminderSettings, 
                first_reminder_days: parseInt(e.target.value) || 7
              })}
            />
            <p className="text-xs text-gray-500">
              Standard: 7 Tage nach Fälligkeitsdatum
            </p>
          </div>

          <div className="space-y-2">
            <Label>2. Erinnerung (Tage nach Fälligkeit)</Label>
            <Input
              type="number"
              min="1"
              max="60"
              value={reminderSettings.second_reminder_days}
              onChange={(e) => setReminderSettings({
                ...reminderSettings, 
                second_reminder_days: parseInt(e.target.value) || 14
              })}
            />
            <p className="text-xs text-gray-500">
              Standard: 14 Tage nach Fälligkeitsdatum
            </p>
          </div>

          <div className="space-y-2">
            <Label>3. Erinnerung (Optional)</Label>
            <Input
              type="number"
              min="1"
              max="90"
              value={reminderSettings.third_reminder_days || ''}
              placeholder="Deaktiviert"
              onChange={(e) => setReminderSettings({
                ...reminderSettings, 
                third_reminder_days: e.target.value ? parseInt(e.target.value) : null
              })}
            />
            <p className="text-xs text-gray-500">
              Optional: Leer lassen um zu deaktivieren
            </p>
          </div>
        </div>

        {/* Max Reminders */}
        <div className="space-y-2">
          <Label>Maximale Anzahl Erinnerungen pro Rechnung</Label>
          <Select
            value={reminderSettings.max_reminders.toString()}
            onValueChange={(value) => setReminderSettings({
              ...reminderSettings, 
              max_reminders: parseInt(value)
            })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 Erinnerung</SelectItem>
              <SelectItem value="2">2 Erinnerungen</SelectItem>
              <SelectItem value="3">3 Erinnerungen</SelectItem>
              <SelectItem value="4">4 Erinnerungen</SelectItem>
              <SelectItem value="5">5 Erinnerungen</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500">
            Nach dieser Anzahl werden keine weiteren Erinnerungen mehr gesendet
          </p>
        </div>

        {/* Email Template Selection */}
        <div className="space-y-4">
          <h4 className="font-semibold text-lg">Email-Templates für Erinnerungen</h4>
          <p className="text-sm text-gray-600">
            Wählen Sie für jede Erinnerung ein eigenes Email-Template. So können Sie die Dringlichkeit steigern.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* 1. Erinnerung Template */}
            <div className="space-y-2">
              <Label>Template für 1. Erinnerung</Label>
              <Select
                value={reminderSettings.first_reminder_template_id}
                onValueChange={(value) => setReminderSettings({
                  ...reminderSettings, 
                  first_reminder_template_id: value
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Template wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {emailTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Freundliche erste Erinnerung
              </p>
            </div>

            {/* 2. Erinnerung Template */}
            <div className="space-y-2">
              <Label>Template für 2. Erinnerung</Label>
              <Select
                value={reminderSettings.second_reminder_template_id}
                onValueChange={(value) => setReminderSettings({
                  ...reminderSettings, 
                  second_reminder_template_id: value
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Template wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {emailTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Dringlichere zweite Erinnerung
              </p>
            </div>

            {/* 3. Erinnerung Template */}
            <div className="space-y-2">
              <Label>Template für 3. Erinnerung (Optional)</Label>
              <Select
                value={reminderSettings.third_reminder_template_id || 'none'}
                onValueChange={(value) => setReminderSettings({
                  ...reminderSettings, 
                  third_reminder_template_id: value === 'none' ? null : value
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Template wählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine / Deaktiviert</SelectItem>
                  {emailTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Letzte Mahnung (optional)
              </p>
            </div>
          </div>
        </div>

        {/* Communication Channels */}
        <div className="space-y-4">
          <h4 className="font-semibold text-lg">Versandkanäle</h4>
          
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <h5 className="font-medium">Email-Erinnerungen</h5>
              <p className="text-sm text-gray-600">
                Erinnerungen per Email versenden
              </p>
            </div>
            <Switch
              checked={reminderSettings.send_email}
              onCheckedChange={(checked) => setReminderSettings({
                ...reminderSettings, 
                send_email: checked
              })}
            />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <h5 className="font-medium">WhatsApp-Erinnerungen</h5>
              <p className="text-sm text-gray-600">
                Erinnerungen per WhatsApp versenden (benötigt Template)
              </p>
            </div>
            <Switch
              checked={reminderSettings.send_whatsapp}
              onCheckedChange={(checked) => setReminderSettings({
                ...reminderSettings, 
                send_whatsapp: checked
              })}
            />
          </div>
        </div>

        {/* Admin-Eskalation */}
        <div className="space-y-4">
          <h4 className="font-semibold text-lg">Admin-Benachrichtigung nach letzter Erinnerung</h4>
          <p className="text-sm text-gray-600">
            Bleibt eine Rechnung trotz aller Erinnerungen offen, wird der Admin einmalig
            per Email und Glocken-Benachrichtigung informiert (mit Kundenname und Betrag).
          </p>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <h5 className="font-medium">Admin-Benachrichtigung aktivieren</h5>
              <p className="text-sm text-gray-600">
                Einmalige Meldung pro Rechnung an den Admin
              </p>
            </div>
            <Switch
              data-testid="escalation-enabled-switch"
              checked={reminderSettings.escalation_enabled}
              onCheckedChange={(checked) => setReminderSettings({
                ...reminderSettings,
                escalation_enabled: checked
              })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Tage nach letzter Erinnerung</Label>
              <Input
                type="number"
                min="1"
                max="60"
                data-testid="escalation-days-input"
                value={reminderSettings.escalation_days}
                onChange={(e) => setReminderSettings({
                  ...reminderSettings,
                  escalation_days: parseInt(e.target.value) || 5
                })}
              />
              <p className="text-xs text-gray-500">
                Standard: 5 Tage nach der letzten Erinnerung
              </p>
            </div>
            <div className="space-y-2">
              <Label>Admin-Email (optional)</Label>
              <Input
                type="email"
                data-testid="escalation-email-input"
                placeholder="Leer = Firmen-Email aus Rechnungs-Einstellungen"
                value={reminderSettings.escalation_email || ''}
                onChange={(e) => setReminderSettings({
                  ...reminderSettings,
                  escalation_email: e.target.value
                })}
              />
              <p className="text-xs text-gray-500">
                Leer lassen, um die Firmen-Email zu verwenden
              </p>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Hinweis:</strong> Erinnerungen werden nur für Rechnungen mit Status &quot;Ausstehend&quot; versendet. 
            Verwenden Sie den Button &quot;Automatische Erinnerungen senden&quot; auf der Rechnungsseite, um alle fälligen 
            Erinnerungen auf einmal zu versenden.
          </p>
        </div>

        {/* Save Button */}
        <Button onClick={saveReminderSettings} disabled={loading} className="w-full">
          <Save className="mr-2 h-4 w-4" />
          Einstellungen speichern
        </Button>
      </CardContent>
    </Card>
  );
};

export default PaymentRemindersSettings;
